import { registerPlugin } from '@capacitor/core';

export interface NoticeItem {
  icon: string;
  color: string;
  text: string;
}

// Pin shown in the large widget — time label, title, owner hue.
export interface WidgetPin {
  time: string;         // "Now", "19:15", "All day"
  title: string;
  color: string;        // hex (#rrggbb)
  ownerInitial: string; // "A"
}

// The pill shown on the medium widget.
export interface WidgetNextEvent {
  title: string;
  inLabel: string;      // "in 32m", "Now"
  color?: string;       // hex (#rrggbb)
}

export type WidgetTone = 'ink' | 'ember' | 'sage' | 'cream';

export interface NoticeBoardData {
  // Legacy — kept so older native builds continue to render.
  greeting: string;
  firstName: string;
  dateString: string;
  notices: NoticeItem[];
  updatedAt: string;

  // Widget-system redesign fields. All optional for backward compat.
  clockTime?: string;        // "18:43"
  timezoneLabel?: string;    // "London"
  completedCount?: number;
  totalCount?: number;
  nextEvent?: WidgetNextEvent | null;
  pins?: WidgetPin[];
  tone?: WidgetTone;
}

export interface NoticeBoardWidgetPlugin {
  updateWidgetData(data: { payload: string }): Promise<void>;
  storeAuthToken(data: { token: string; refreshToken: string; apiUrl: string }): Promise<void>;
}

const NoticeBoardWidget = registerPlugin<NoticeBoardWidgetPlugin>('NoticeBoardWidget');

// iOS-only native plugin wrapper.
// On web/Android/older iOS builds where the plugin isn't registered, no-op
// so app features don't spam client-error logs or reject unrelated flows.
const noop = async () => {};
const safeNoticeBoardWidget: NoticeBoardWidgetPlugin = {
  async updateWidgetData(data: { payload: string }) {
    try {
      await NoticeBoardWidget.updateWidgetData(data);
    } catch {
      await noop();
    }
  },
  async storeAuthToken(data: { token: string; refreshToken: string; apiUrl: string }) {
    try {
      await NoticeBoardWidget.storeAuthToken(data);
    } catch {
      await noop();
    }
  },
};

export default safeNoticeBoardWidget;
